import React, { useState, useEffect } from 'react';

interface PlanEntry {
    day: number;
    date: string;
    display: string;
    references: Array<{ text: string; bookId: string; chapter: number }>;
}

interface ReadingPlanCalendarProps {
    planId: string;
    planName: string;
    onNavigate: (bookId: string, chapter: number) => void;
}

export function ReadingPlanCalendar({ planId, planName, onNavigate }: ReadingPlanCalendarProps) {
    const [entries, setEntries] = useState<PlanEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [resolvedName, setResolvedName] = useState(planName);

    useEffect(() => {
        setLoading(true);
        fetch(`/api/reading-plans/${planId}/full`)
            .then(r => r.json())
            .then(data => {
                setEntries(data.entries || []);
                if (data.name) setResolvedName(data.name);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [planId]);

    // Auto-scroll to today's entry
    useEffect(() => {
        if (!loading && entries.length > 0) {
            setTimeout(() => {
                const todayEl = document.querySelector('.reading-plan-entry-today');
                if (todayEl) {
                    todayEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    }, [loading, entries]);

    if (loading) return <div>Loading plan...</div>;

    const monthGroups = groupByMonth(entries);
    const today = new Date().toISOString().slice(0, 10);

    return (
        <article>
            <header>
                <h1 className="bible-book-title">{resolvedName} Reading Plan</h1>
                <p className="reading-plan-calendar-subtitle">
                    {entries.length} readings for {new Date().getFullYear()}
                </p>
            </header>

            {monthGroups.map(({ month, monthEntries }) => (
                <section key={month} className="reading-plan-month">
                    <h2 className="reading-plan-month-title">{month}</h2>
                    <div className="reading-plan-month-entries">
                        {monthEntries.map(entry => {
                            const isToday = entry.date === today;
                            const dateObj = new Date(entry.date + 'T12:00:00');
                            const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                            const dayNum = dateObj.getDate();

                            return (
                                <div
                                    key={entry.day}
                                    className={`reading-plan-entry${isToday ? ' reading-plan-entry-today' : ''}`}
                                >
                                    <span className="reading-plan-entry-date">
                                        {dayOfWeek} {dayNum}
                                    </span>
                                    <span className="reading-plan-entry-refs">
                                        {entry.references.map((r, i) => (
                                            <span key={i}>
                                                {i > 0 && '; '}
                                                <a
                                                    href={`/${r.bookId}/${r.chapter}`}
                                                    className="reading-plan-link"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        onNavigate(r.bookId, r.chapter);
                                                    }}
                                                >
                                                    {r.text}
                                                </a>
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </section>
            ))}
        </article>
    );
}

function groupByMonth(entries: PlanEntry[]): Array<{ month: string; monthEntries: PlanEntry[] }> {
    const groups: Map<string, PlanEntry[]> = new Map();
    for (const entry of entries) {
        const dateObj = new Date(entry.date + 'T12:00:00');
        const monthKey = dateObj.toLocaleDateString('en-US', { month: 'long' });
        if (!groups.has(monthKey)) groups.set(monthKey, []);
        groups.get(monthKey)!.push(entry);
    }
    return Array.from(groups.entries()).map(([month, monthEntries]) => ({ month, monthEntries }));
}
